/**
 * Commitments: what the business would have to change, not a better way of saying the same thing.
 *
 * Offered under a concern theme, because that is where the question arises. Every option is judged
 * before it can be adopted - an option that only softens how an existing term is described fails that
 * check and cannot be taken. Adopting one creates a version whose locked facts change with it; it is
 * not run, because running costs money.
 */

import { Handshake } from "lucide-react";
import { toast } from "sonner";

import { isFinished } from "@/api/jobs";
import { Button } from "@/components/ui/button";
import { useAdoptCommitment, useCommitments, useJobs, useProposeCommitment } from "@/lib/queries";

export function Commitments({ studyId, runId, themeId }: { studyId: string; runId: string; themeId: string }) {
  const proposals = useCommitments(studyId).data ?? [];
  const propose = useProposeCommitment(studyId);
  const adopt = useAdoptCommitment(studyId);
  const jobs = useJobs(studyId).data ?? [];
  const busy = jobs.some((j) => !isFinished(j));
  const running = jobs.some((j) => j.kind === "commitment" && !isFinished(j));
  const mine = proposals.filter((p) => p.theme_id === themeId && p.run_id === runId);

  return (
    <div className="mt-2 border-t border-white/10 pt-2">
      <Button
        size="xs"
        variant="ghost"
        disabled={busy || propose.isPending}
        onClick={() =>
          propose.mutate(
            { run_id: runId, theme_id: themeId },
            { onError: (error: unknown) => toast.error(error instanceof Error ? error.message : "could not ask") },
          )
        }
      >
        <Handshake aria-hidden="true" />
        {running ? "Working…" : "What would have to change?"}
      </Button>
      <p className="mt-1 text-xs text-muted-foreground">
        Asks what the business would have to do differently to address this concern - a changed price, term or
        guarantee, not a changed wording.
      </p>

      {mine.map((proposal) => (
        <ul key={proposal.proposal_id} className="mt-2 space-y-2">
          {proposal.options.map((option, index) => (
            <li key={`${proposal.proposal_id}-${index}`} className="rounded-lg border border-white/10 bg-black/20 p-2">
              <p className="text-sm font-medium">{option.title}</p>
              <p className="mt-1 text-xs">{option.real_world_change}</p>
              <p className="mt-1 text-xs text-muted-foreground">What it would take: {option.feasibility}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                New promise: <span className="text-foreground">{option.new_locked_fact}</span>
                {option.replaces_locked_fact && ` (replaces “${option.replaces_locked_fact}”)`}
              </p>
              {option.accepted ? (
                option.adopted_variant_id ? (
                  <p className="mt-1 text-xs text-step-done">Adopted as {option.adopted_variant_id}</p>
                ) : (
                  <Button
                    size="xs"
                    className="mt-1"
                    disabled={adopt.isPending}
                    onClick={() =>
                      adopt.mutate(
                        { proposal_id: proposal.proposal_id, option: index },
                        {
                          onSuccess: (made) =>
                            toast.success(`Version ${made.variant_id} makes this promise - run it to see how it lands`),
                          onError: (error: unknown) =>
                            toast.error(error instanceof Error ? error.message : "could not adopt"),
                        },
                      )
                    }
                  >
                    Make this promise
                  </Button>
                )
              ) : (
                <p className="mt-1 text-xs text-[#ff8f75]">
                  Rejected: {option.reasons.join("; ") || "this is a wording change, not a real one"}
                </p>
              )}
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}
